import { SnComponentUpdater } from '@lib/core';

import { SnSmartButtonGroupEventType } from '../enums/smart-button-group-event-type.enum';
import { SnSmartButtonConfig } from '../types/smart-button-config';
import { SnSmartButtonGroupEvent } from '../types/smart-button-group-event';
import { SnSmartButton } from './smart-button.model';

export class SnSmartButtonGroup {
  private _buttons: SnSmartButton[] = [];
  private readonly _buttonMap = new Map<string, SnSmartButton>();

  private _isVisible = true;

  constructor(
    public readonly name: string,
    private readonly _componentUpdater: SnComponentUpdater<SnSmartButtonGroupEvent>,
  ) {}

  get isVisible(): boolean {
    return this._isVisible;
  }
  get isEmpty(): boolean {
    return !this._buttonMap.size;
  }
  get buttons(): SnSmartButton[] {
    return this._buttons;
  }

  public setButtons(...items: SnSmartButtonConfig[]): void {
    items.forEach((item) => {
      this._buttonMap.set(item.name, new SnSmartButton(item.name, item));
    });

    this._onListUpdated();
  }

  public deleteButton(name: string): boolean {
    if (this._buttonMap.delete(name)) {
      this._onListUpdated();
      return true;
    }

    return false;
  }

  public disableButton(name: string): void {
    const button = this._buttonMap.get(name);

    if (button) {
      button.disabled = true;
      this._onListUpdated();
    }
  }

  public enableButton(name: string): void {
    const button = this._buttonMap.get(name);

    if (button) {
      button.disabled = false;
      this._onListUpdated();
    }
  }

  public show(): void {
    if (!this._isVisible) {
      this._isVisible = true;
      this._onVisibleChanged(true);
    }
  }

  public hide(): void {
    if (this._isVisible) {
      this._isVisible = false;
      this._onVisibleChanged(false);
    }
  }

  public clear(): void {
    this._buttonMap.clear();
    this._onListUpdated();
  }

  private _onListUpdated(): void {
    this._buttons = Array.from(this._buttonMap.values());

    this._componentUpdater.updateComponent('sn-smart-button-group', {
      name: this.name,
      type: SnSmartButtonGroupEventType.Update,
      buttons: this._buttons,
    });
  }

  private _onVisibleChanged(visible: boolean): void {
    this._componentUpdater.updateComponent('sn-smart-button-group', {
      name: this.name,
      type: SnSmartButtonGroupEventType.Visibility,
      visible,
    });
  }
}
